/**
 * GRANULLO PLATFORM EVOLUTION — System Memory Context
 * 
 * Phase 7: System Memory Ingestion Layer
 * 
 * ARCHITECTURAL INVARIANT:
 * System Memory ingests STRUCTURAL INTELLIGENCE ONLY.
 * It derives from canonical program state and engine outputs.
 * It does NOT duplicate or replace the Program Object.
 * 
 * SAFE TO INGEST:
 * - topology signatures
 * - blueprint structure fingerprints
 * - signal behavior profiles
 * - decision outcomes
 * - intervention outcomes
 * - exposure patterns
 * - cascade patterns
 * - stress response patterns
 * - commit utilization patterns
 * - economic version references
 * 
 * UNSAFE / PROHIBITED:
 * - PII
 * - beneficiary identities
 * - merchant identities (unless structurally abstracted)
 * - raw personal data
 * - ad hoc UI state
 * 
 * This is the foundation for institutional intelligence.
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

// ============================================================================
// MEMORY RECORD TYPES
// ============================================================================

/**
 * Memory Record Types - What System Memory can capture
 */
export const MEMORY_RECORD_TYPES = {
  TOPOLOGY_SIGNATURE: 'topology_signature',
  SIGNAL_PROFILE: 'signal_profile',
  DECISION_OUTCOME: 'decision_outcome',
  INTERVENTION_PATTERN: 'intervention_pattern',
  EXPOSURE_SIGNATURE: 'exposure_signature',
  CASCADE_SIGNATURE: 'cascade_signature',
  STRESS_PROFILE: 'stress_profile',
  COMMIT_PROFILE: 'commit_profile',
  BLUEPRINT_LINEAGE: 'blueprint_lineage',
  ECONOMIC_VERSION_REF: 'economic_version_ref'
};

/**
 * Memory Record Schema
 */
const createMemoryRecord = (type, programId, signature, metadata = {}) => ({
  record_id: `mem_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  record_type: type,
  program_id: programId,
  signature,
  metadata,
  captured_at: new Date().toISOString(),
  version: '1.0'
});

// ============================================================================
// SIGNATURE GENERATORS (Structural Intelligence Only)
// ============================================================================

/**
 * Generate Topology Signature
 * Captures structural characteristics without PII
 */
export function generateTopologySignature(blueprint) {
  if (!blueprint) return null;
  
  const nodes = blueprint.nodes || [];
  const edges = blueprint.edges || [];
  
  // Count node types (structural only)
  const nodeTypeCounts = nodes.reduce((acc, node) => {
    const type = node.type || 'unknown';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});
  
  // Calculate structural metrics
  const layerDepth = Math.max(...nodes.map(n => n.layer || 0), 0) + 1;
  const edgeDensity = nodes.length > 1 ? edges.length / (nodes.length * (nodes.length - 1) / 2) : 0;
  
  return {
    node_count: nodes.length,
    edge_count: edges.length,
    node_type_distribution: nodeTypeCounts,
    layer_depth: layerDepth,
    edge_density: Math.round(edgeDensity * 100) / 100,
    has_authority_nodes: nodes.some(n => n.type === 'authority'),
    has_merchant_nodes: nodes.some(n => n.type === 'merchant'),
    has_beneficiary_nodes: nodes.some(n => n.type === 'beneficiary'),
    structural_hash: `topo_${nodes.length}_${edges.length}_${layerDepth}`
  };
}

/**
 * Generate Signal Profile
 * Captures signal behavior characteristics
 */
export function generateSignalProfile(signals) {
  if (!signals || signals.length === 0) return null;
  
  const typeDistribution = signals.reduce((acc, signal) => {
    const type = signal.signal_type || 'unknown';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});
  
  const severityDistribution = signals.reduce((acc, signal) => {
    const severity = signal.severity || 'informational';
    acc[severity] = (acc[severity] || 0) + 1;
    return acc;
  }, {});
  
  return {
    total_signals: signals.length,
    type_distribution: typeDistribution,
    severity_distribution: severityDistribution,
    structural_count: typeDistribution.structural || 0,
    behaviour_count: typeDistribution.behaviour || 0,
    informational_count: typeDistribution.informational || 0,
    profile_hash: `sig_${signals.length}_${Object.keys(typeDistribution).length}`
  };
}

/**
 * Generate Exposure Signature
 * Captures exposure pattern characteristics
 */
export function generateExposureSignature(exposureData) {
  if (!exposureData) return null;
  
  return {
    governance_concentration: exposureData.governanceConcentration || 0,
    authority_bottleneck: exposureData.authorityBottleneck || 0,
    monitoring_blind_spot: exposureData.monitoringBlindSpot || 0,
    systemic_overload: exposureData.systemicOverload || 0,
    regional_instability: exposureData.regionalInstability || 0,
    overall_score: exposureData.overallScore || 0,
    exposure_hash: `exp_${Math.round((exposureData.overallScore || 0) * 100)}`
  };
}

/**
 * Generate Cascade Signature
 * Captures cascade risk patterns
 */
export function generateCascadeSignature(cascadeData) {
  if (!cascadeData) return null;
  
  return {
    propagation_paths: cascadeData.propagationPaths?.length || 0,
    vulnerable_nodes: cascadeData.vulnerableNodes?.length || 0,
    bottleneck_nodes: cascadeData.bottleneckNodes?.length || 0,
    max_cascade_depth: cascadeData.maxCascadeDepth || 0,
    cascade_risk_score: cascadeData.cascadeRiskScore || 0,
    cascade_hash: `cas_${cascadeData.propagationPaths?.length || 0}_${cascadeData.cascadeRiskScore || 0}`
  };
}

/**
 * Generate Stress Profile
 * Captures system stress patterns
 */
export function generateStressProfile(stressData) {
  if (!stressData) return null;
  
  return {
    stress_index: stressData.stressIndex || 0,
    signal_drift_factor: stressData.factors?.signalDrift || 0,
    decision_backlog_factor: stressData.factors?.decisionBacklog || 0,
    exposure_intensity_factor: stressData.factors?.exposureIntensity || 0,
    cascade_risk_factor: stressData.factors?.cascadeRisk || 0,
    authority_lag_factor: stressData.factors?.authorityLag || 0,
    commit_pressure_factor: stressData.factors?.commitPressure || 0,
    monitoring_burden_factor: stressData.factors?.monitoringBurden || 0,
    stress_hash: `str_${Math.round((stressData.stressIndex || 0) * 100)}`
  };
}

/**
 * Generate Commit Profile
 * Captures commit utilization patterns
 */
export function generateCommitProfile(commitData) {
  if (!commitData) return null;
  
  return {
    topology_commits: commitData.grid?.topology || 0,
    throughput_commits: commitData.grid?.throughput || 0,
    monitoring_commits: commitData.grid?.monitoring || 0,
    total_commits: commitData.totalCommits || 0,
    envelope_limit: commitData.envelopeLimit || 0,
    utilization_percent: commitData.envelopeLimit > 0 
      ? Math.round((commitData.totalCommits / commitData.envelopeLimit) * 10000) / 100
      : 0,
    economic_version: commitData.economicVersion || 'v1.0',
    envelope_compliant: commitData.envelopeCompliant || false,
    commit_hash: `com_${commitData.totalCommits || 0}_${commitData.economicVersion || 'v1.0'}`
  };
}

/**
 * Generate Decision Outcome Record
 * Captures decision resolution patterns
 */
export function generateDecisionOutcome(decision) {
  if (!decision) return null;
  
  return {
    decision_type: decision.type || 'unknown',
    triggering_signal_type: decision.triggering_signal?.type || 'unknown',
    deviation_level: decision.deviation_level || 'warning',
    resolution_status: decision.decision_status || 'pending',
    resolution_time_hours: decision.resolution_time_hours || null,
    escalation_count: decision.escalation_chain?.length || 0,
    authority_level: decision.authority_responsible || 'local',
    outcome_hash: `dec_${decision.deviation_level}_${decision.decision_status}`
  };
}

/**
 * Generate Intervention Pattern
 * Captures intervention effectiveness patterns
 */
export function generateInterventionPattern(intervention) {
  if (!intervention) return null;
  
  return {
    intervention_type: intervention.type || 'unknown',
    target_dimension: intervention.target_dimension || 'unknown',
    expected_impact: intervention.expected_impact || 0,
    actual_impact: intervention.actual_impact || null,
    success_rate: intervention.success_rate || null,
    pattern_hash: `int_${intervention.type}_${intervention.target_dimension}`
  };
}

// ============================================================================
// SYSTEM MEMORY CONTEXT
// ============================================================================

const SystemMemoryContext = createContext(null);

export function SystemMemoryProvider({ children }) {
  // Memory store - keyed by program_id
  const [memoryStore, setMemoryStore] = useState({});
  
  // Memory statistics
  const [memoryStats, setMemoryStats] = useState({
    totalRecords: 0,
    recordsByType: {},
    lastIngestionAt: null
  });
  
  /**
   * Ingest a memory record
   * This is the primary ingestion point
   */
  const ingestRecord = useCallback((type, programId, signature, metadata = {}) => {
    if (!type || !programId || !signature) {
      console.warn('[SystemMemory] Invalid ingestion attempt:', { type, programId, signature });
      return null;
    }
    
    // Validate record type
    if (!Object.values(MEMORY_RECORD_TYPES).includes(type)) {
      console.warn('[SystemMemory] Unknown record type:', type);
      return null;
    }
    
    const record = createMemoryRecord(type, programId, signature, metadata);
    
    setMemoryStore(prev => {
      const programMemory = prev[programId] || { records: [], lastUpdated: null };
      return {
        ...prev,
        [programId]: {
          records: [...programMemory.records, record],
          lastUpdated: record.captured_at
        }
      };
    });
    
    setMemoryStats(prev => ({
      totalRecords: prev.totalRecords + 1,
      recordsByType: {
        ...prev.recordsByType,
        [type]: (prev.recordsByType[type] || 0) + 1
      },
      lastIngestionAt: record.captured_at
    }));
    
    return record;
  }, []);
  
  /**
   * Ingest topology signature for a program
   */
  const ingestTopology = useCallback((programId, blueprint, metadata = {}) => {
    const signature = generateTopologySignature(blueprint);
    if (!signature) return null;
    return ingestRecord(MEMORY_RECORD_TYPES.TOPOLOGY_SIGNATURE, programId, signature, metadata);
  }, [ingestRecord]);
  
  /**
   * Ingest signal profile for a program
   */
  const ingestSignalProfile = useCallback((programId, signals, metadata = {}) => {
    const signature = generateSignalProfile(signals);
    if (!signature) return null;
    return ingestRecord(MEMORY_RECORD_TYPES.SIGNAL_PROFILE, programId, signature, metadata);
  }, [ingestRecord]);
  
  /**
   * Ingest exposure signature for a program
   */
  const ingestExposure = useCallback((programId, exposureData, metadata = {}) => {
    const signature = generateExposureSignature(exposureData);
    if (!signature) return null;
    return ingestRecord(MEMORY_RECORD_TYPES.EXPOSURE_SIGNATURE, programId, signature, metadata);
  }, [ingestRecord]);
  
  /**
   * Ingest cascade signature for a program
   */
  const ingestCascade = useCallback((programId, cascadeData, metadata = {}) => {
    const signature = generateCascadeSignature(cascadeData);
    if (!signature) return null;
    return ingestRecord(MEMORY_RECORD_TYPES.CASCADE_SIGNATURE, programId, signature, metadata);
  }, [ingestRecord]);
  
  /**
   * Ingest stress profile for a program
   */
  const ingestStressProfile = useCallback((programId, stressData, metadata = {}) => {
    const signature = generateStressProfile(stressData);
    if (!signature) return null;
    return ingestRecord(MEMORY_RECORD_TYPES.STRESS_PROFILE, programId, signature, metadata);
  }, [ingestRecord]);
  
  /**
   * Ingest commit profile for a program
   */
  const ingestCommitProfile = useCallback((programId, commitData, metadata = {}) => {
    const signature = generateCommitProfile(commitData);
    if (!signature) return null;
    return ingestRecord(MEMORY_RECORD_TYPES.COMMIT_PROFILE, programId, signature, metadata);
  }, [ingestRecord]);
  
  /**
   * Ingest decision outcome
   */
  const ingestDecisionOutcome = useCallback((programId, decision, metadata = {}) => {
    const signature = generateDecisionOutcome(decision);
    if (!signature) return null;
    return ingestRecord(MEMORY_RECORD_TYPES.DECISION_OUTCOME, programId, signature, metadata);
  }, [ingestRecord]);
  
  /**
   * Ingest intervention pattern
   */
  const ingestInterventionPattern = useCallback((programId, intervention, metadata = {}) => {
    const signature = generateInterventionPattern(intervention);
    if (!signature) return null;
    return ingestRecord(MEMORY_RECORD_TYPES.INTERVENTION_PATTERN, programId, signature, metadata);
  }, [ingestRecord]);
  
  /**
   * Ingest blueprint lineage reference
   */
  const ingestBlueprintLineage = useCallback((programId, lineageRef, metadata = {}) => {
    if (!lineageRef) return null;
    return ingestRecord(MEMORY_RECORD_TYPES.BLUEPRINT_LINEAGE, programId, lineageRef, metadata);
  }, [ingestRecord]);
  
  /**
   * Ingest economic version reference
   */
  const ingestEconomicVersion = useCallback((programId, economicVersion, metadata = {}) => {
    if (!economicVersion) return null;
    return ingestRecord(MEMORY_RECORD_TYPES.ECONOMIC_VERSION_REF, programId, { version: economicVersion }, metadata);
  }, [ingestRecord]);
  
  /**
   * Get all memory records for a program
   */
  const getProgramMemory = useCallback((programId) => {
    return memoryStore[programId] || { records: [], lastUpdated: null };
  }, [memoryStore]);
  
  /**
   * Get records by type for a program
   */
  const getRecordsByType = useCallback((programId, type) => {
    const programMemory = memoryStore[programId];
    if (!programMemory) return [];
    return programMemory.records.filter(r => r.record_type === type);
  }, [memoryStore]);
  
  /**
   * Get latest record of a type for a program
   */
  const getLatestRecord = useCallback((programId, type) => {
    const records = getRecordsByType(programId, type);
    if (records.length === 0) return null;
    return records[records.length - 1];
  }, [getRecordsByType]);
  
  /**
   * Get memory summary for benchmarking
   */
  const getMemorySummary = useCallback((programId) => {
    const programMemory = memoryStore[programId];
    if (!programMemory) return null;
    
    const latestTopology = getLatestRecord(programId, MEMORY_RECORD_TYPES.TOPOLOGY_SIGNATURE);
    const latestSignals = getLatestRecord(programId, MEMORY_RECORD_TYPES.SIGNAL_PROFILE);
    const latestExposure = getLatestRecord(programId, MEMORY_RECORD_TYPES.EXPOSURE_SIGNATURE);
    const latestStress = getLatestRecord(programId, MEMORY_RECORD_TYPES.STRESS_PROFILE);
    const latestCommit = getLatestRecord(programId, MEMORY_RECORD_TYPES.COMMIT_PROFILE);
    const latestLineage = getLatestRecord(programId, MEMORY_RECORD_TYPES.BLUEPRINT_LINEAGE);
    
    return {
      program_id: programId,
      record_count: programMemory.records.length,
      last_updated: programMemory.lastUpdated,
      topology: latestTopology?.signature || null,
      signals: latestSignals?.signature || null,
      exposure: latestExposure?.signature || null,
      stress: latestStress?.signature || null,
      commit: latestCommit?.signature || null,
      lineage: latestLineage?.signature || null
    };
  }, [memoryStore, getLatestRecord]);
  
  // Context value
  const value = useMemo(() => ({
    // Constants
    MEMORY_RECORD_TYPES,
    
    // State
    memoryStore,
    memoryStats,
    
    // Ingestion functions
    ingestRecord,
    ingestTopology,
    ingestSignalProfile,
    ingestExposure,
    ingestCascade,
    ingestStressProfile,
    ingestCommitProfile,
    ingestDecisionOutcome,
    ingestInterventionPattern,
    ingestBlueprintLineage,
    ingestEconomicVersion,
    
    // Query functions
    getProgramMemory,
    getRecordsByType,
    getLatestRecord,
    getMemorySummary,
    
    // Signature generators (for external use)
    generateTopologySignature,
    generateSignalProfile,
    generateExposureSignature,
    generateCascadeSignature,
    generateStressProfile,
    generateCommitProfile
  }), [
    memoryStore,
    memoryStats,
    ingestRecord,
    ingestTopology,
    ingestSignalProfile,
    ingestExposure,
    ingestCascade,
    ingestStressProfile,
    ingestCommitProfile,
    ingestDecisionOutcome,
    ingestInterventionPattern,
    ingestBlueprintLineage,
    ingestEconomicVersion,
    getProgramMemory,
    getRecordsByType,
    getLatestRecord,
    getMemorySummary
  ]);
  
  return (
    <SystemMemoryContext.Provider value={value}>
      {children}
    </SystemMemoryContext.Provider>
  );
}

export function useSystemMemory() {
  const context = useContext(SystemMemoryContext);
  if (!context) {
    throw new Error('useSystemMemory must be used within SystemMemoryProvider');
  }
  return context;
}

/**
 * Optional hook for use outside provider (returns null)
 */
export function useSystemMemoryOptional() {
  return useContext(SystemMemoryContext);
}

export default SystemMemoryContext;
